import {
  borderRadius,
  colors,
  fontSize,
  PillSelector,
  spacing,
  Text,
  TextArea,
} from '@retailer-platform/shared-components'
import React, { type FunctionComponent } from 'react'
import styled from '@emotion/styled'
import {
  type FeatureConfigDetail,
  FeatureConfigDetails,
  FeatureConfigType,
} from '../util/qualityAuditWorkflowConfigFeatureDetails.tsx'
import { Input } from '../../md-platform-list/components/MdPlatformModifyModal.tsx'
import { useDomainMessages } from '../../../utils/domain/intl.ts'
import { PropertySection } from '../../../utils/common.styles.tsx'
import { FeatureDetailPill } from './QualityAuditWorkflowConfigColumnModalFeatureSelect.tsx'

interface Props {
  featureName?: string
  featureValue?: string
  onChange: (value: string) => void
}

export const ConfigPropertySection = styled(PropertySection)({
  gap: `${spacing.X16}`,
  border: `1px dashed ${colors.GRAYSCALE.X30}`,
  padding: `${spacing.X20}`,
  borderRadius: borderRadius.X8,
})

export const ConfigSectionTitle = styled.div({
  fontSize: 'larger',
  fontWeight: 600,
  borderBottom: `1px dashed ${colors.GRAYSCALE.X30}`,
  paddingBottom: `${spacing.X20}`,
})

export const QualityAuditWorkflowConfigColumnModalFeatureInput: FunctionComponent<
  React.PropsWithChildren<Props>
> = ({ featureName, featureValue, onChange }) => {
  const i18n = useDomainMessages({
    valueDetails: 'catalogAdminDomain.qualityAuditWorkflows.configs.feature.modal.value.details',
  })
  const details: FeatureConfigDetail | null = featureName
    ? FeatureConfigDetails?.[featureName]
    : null
  const featureType = details?.type ?? FeatureConfigType.String

  // Initially, we use a general string input.
  let inputComponent = (
    <TextArea
      style={{
        width: '100%',
        fontSize: fontSize.X15,
        resize: 'vertical',
        height: '20vh',
      }}
      value={featureValue}
      onChange={event => onChange(event.target.value)}
    />
  )
  if (featureType === FeatureConfigType.Int) {
    inputComponent = (
      <Input
        style={{ width: '100%' }}
        value={(featureValue ?? '').toLowerCase().trim()}
        onChange={e => {
          onChange(`${e.target.value}`)
        }}
        type={'number'}
      />
    )
  } else if (featureType === FeatureConfigType.Custom) {
    const Component = details?.customComponent
    if (Component) {
      inputComponent = (
        <Component
          featureValue={(featureValue ?? '').toLowerCase().trim()}
          onChange={value => {
            onChange(`${value}`)
          }}
        />
      )
    }
  } else if (featureType === FeatureConfigType.Boolean || featureType === FeatureConfigType.Enum) {
    const enumOptions = (details?.enums ?? []).map(value => ({ label: value, value: value }))
    inputComponent = (
      <div style={{ paddingTop: `${spacing.X16}` }}>
        <PillSelector
          items={
            featureType === FeatureConfigType.Enum && !!details
              ? enumOptions
              : [
                  { label: 'True', value: 'true' },
                  { label: 'False', value: 'false' },
                ]
          }
          value={(featureValue ?? '').toLowerCase().trim()}
          onChange={e => {
            onChange(e.target.value)
          }}
        />
      </div>
    )
  }

  return (
    <>
      <Text
        size="small"
        css={{
          display: 'flex',
          flex: 1,
          flexDirection: 'row',
          gap: `${spacing.X8}`,
          alignItems: 'center',
        }}
      >
        {details?.shortDescription ?? i18n.valueDetails}
        <FeatureDetailPill>{featureType}</FeatureDetailPill>
      </Text>
      {inputComponent}
    </>
  )
}
